import Highlight from "@tiptap/extension-highlight";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import { EditorProvider, useCurrentEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Eraser,
  Highlighter,
  Italic,
  Minus,
  Redo2,
  Strikethrough,
  Undo2,
} from "lucide-react";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "./ui/button";

export default function EditorTextArea({
  setForm,
  form,
  disabled = false,
  EditorRerender,
}) {
  const MenuBar = () => {
    const { editor } = useCurrentEditor();

    if (!editor) {
      return null;
    }

    return (
      <div className="pb-5">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={"outline"}
            size={"icon"}
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={
              disabled || !editor.can().chain().focus().toggleBold().run()
            }
            className={` ${
              editor.isActive("bold") &&
              "bg-blue-600 hover:bg-blue-600/80 text-white hover:text-white"
            } `}
          >
            <Bold />
          </Button>
          <Button
            variant={"outline"}
            size={"icon"}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={
              disabled || !editor.can().chain().focus().toggleItalic().run()
            }
            className={`${
              editor.isActive("italic") &&
              "bg-blue-600 hover:bg-blue-600/80 text-white hover:text-white"
            } `}
          >
            <Italic />
          </Button>
          <Button
            variant={"outline"}
            size={"icon"}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={
              disabled || !editor.can().chain().focus().toggleStrike().run()
            }
            className={`${
              editor.isActive("strike") &&
              "bg-blue-600 hover:bg-blue-600/80 text-white hover:text-white"
            } `}
          >
            <Strikethrough />
          </Button>
          <Button
            variant={"outline"}
            size={"icon"}
            onClick={() => editor.chain().focus().toggleCode().run()}
            disabled={
              disabled || !editor.can().chain().focus().toggleCode().run()
            }
            className={`${
              editor.isActive("code") &&
              "bg-blue-600 hover:bg-blue-600/80 text-white hover:text-white"
            } `}
          >
            <Code />
          </Button>
          <Button
            variant={"outline"}
            size={"icon"}
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            disabled={disabled}
          >
            <Minus />
          </Button>
          <Button
            variant={"outline"}
            size={"icon"}
            onClick={() => editor.chain().focus().unsetAllMarks().run()}
            disabled={disabled}
          >
            <Eraser />
          </Button>
          <Button
            variant={"outline"}
            size={"icon"}
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            className={
              editor.isActive({ textAlign: "left" }) &&
              "bg-blue-600 hover:bg-blue-600/80 text-white hover:text-white"
            }
            disabled={disabled}
          >
            <AlignLeft />
          </Button>
          <Button
            variant={"outline"}
            size={"icon"}
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            className={
              editor.isActive({ textAlign: "center" }) &&
              "bg-blue-600 hover:bg-blue-600/80 text-white hover:text-white"
            }
            disabled={disabled}
          >
            <AlignCenter />
          </Button>
          <Button
            variant={"outline"}
            size={"icon"}
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            className={
              editor.isActive({ textAlign: "right" }) &&
              "bg-blue-600 hover:bg-blue-600/80 text-white hover:text-white"
            }
            disabled={disabled}
          >
            <AlignRight />
          </Button>
          <Button
            variant={"outline"}
            size={"icon"}
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            className={
              editor.isActive("highlight") &&
              "bg-blue-600 hover:bg-blue-600/80 text-white hover:text-white"
            }
            disabled={disabled}
          >
            <Highlighter />
          </Button>
          <Button
            variant={"outline"}
            size={"icon"}
            onClick={() => editor.chain().focus().undo().run()}
            disabled={disabled || !editor.can().chain().focus().undo().run()}
          >
            <Undo2 />
          </Button>
          <Button
            variant={"outline"}
            size={"icon"}
            onClick={() => editor.chain().focus().redo().run()}
            disabled={disabled || !editor.can().chain().focus().redo().run()}
          >
            <Redo2 />
          </Button>
        </div>
      </div>
    );
  };

  const extensions = [
    Placeholder.configure({
      dir: "rtl",
      placeholder: "أدخل الوصف المهمة",
    }),
    TextAlign.configure({
      types: ["heading", "paragraph"],
    }),
    Highlight,
    StarterKit.configure({
      bulletList: {
        keepMarks: false,
        keepAttributes: false,
      },
      orderedList: {
        keepMarks: false,
        keepAttributes: false,
      },
    }),
    TextStyle,
    Paragraph.extend({
      addAttributes() {
        return {
          dir: {
            default: "rtl",
            parseHTML: (element) => element.getAttribute("rtl") || "rtl",
            renderHTML: (attributes) => ({ dir: attributes.dir }),
          },
        };
      },
    }),
  ];

  return (
    <div className="border rounded-xl shadow-sm p-4 flex-1 flex flex-col">
      <EditorProvider
        key={[disabled, EditorRerender]}
        editable={!disabled}
        slotBefore={<MenuBar />}
        extensions={extensions}
        content={form.description}
        onUpdate={({ editor }) => {
          setForm({
            ...form,
            description: editor.getHTML(),
          });
        }}
      ></EditorProvider>
    </div>
  );
}
